import { DirectUploadConfig, FileRules, UploadError } from '@execonline-inc/file-upload.private';
import { Header } from 'ajaxian';
import { Task } from 'taskarian';
import { AppyError, clientHeadersT } from '../Appy';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';

/**
 * Everything that can go wrong between the user picking a file and the upload
 * being ready to submit. Building the config can fail before an upload even
 * starts, so both halves share one error type and the caller handles one
 * exhaustive switch.
 */
export type DirectUploadFailure = AppyError | UploadError;

/**
 * Rules for the learning collection logo. They mirror the
 * `learning_collection_logo` entry in DirectUpload::Purpose on the server,
 * which is the gate. These run first so the user learns a file is wrong
 * before it is read and hashed.
 */
export const collectionLogoRules: FileRules = {
  extensions: ['jpg', 'jpeg', 'png', 'gif'],
  maxBytes: 5 * 1024 * 1024,
  maxFiles: 1,
};

const toHeaderRecord = (headers: ReadonlyArray<Header>): Record<string, string> =>
  headers.reduce((soFar, h) => ({ ...soFar, [h.field]: h.value }), {});

/**
 * Builds the upload config from a resource's `direct-upload` link and the same
 * auth headers every other API call sends. The endpoint runs inside the
 * self-service application controller, so it needs them.
 */
export const directUploadConfig = (
  link: Link,
  purpose: string,
): Task<DirectUploadFailure, DirectUploadConfig> =>
  clientHeadersT()
    .mapError<DirectUploadFailure>((error) => error)
    .map((headers) => ({
      endpoint: link.href,
      purpose,
      withCredentials: true,
      headers: toHeaderRecord(headers),
    }));

export const logoErrorMessage = (error: DirectUploadFailure): TPlainTextKey => {
  switch (error.kind) {
    case 'unsupported-file-type':
    case 'file-too-large':
    case 'empty-file':
      return 'Upload an image file (PNG, JPG, GIF) that is 5mb in size or less';
    case 'missing-application-id':
    case 'missing-api-compatibility':
      return 'Unable to get signed url';
    default:
      return 'Failed to upload the file to s3';
  }
};
